/* eslint-disable max-lines */
import {
  Location,
  LocationAlarmCamera,
  QuickResolveActionItem,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import { Alert } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { Dispatch } from 'react';
import { UnauthorizedFaceDetection } from '../sop-workflow/unauthorized-face-detection';
import { isAlarmGroupResolved, TopSituation } from '../sop-workflow/util';
import { CARD_ROOT_CLASS, CARD_VARIANTS, TalkdownStep } from './sop-card-utils';
import { ActionsRow } from './ActionsRow';
import AIRecommendationCard from './AIRecommendationCard';
import { LastEscalatedCard } from './LastEscalatedCard';
import { SOPCard } from './SOPCard';
import { SOPWorkflowV2ContainerProps } from './SOPWorkflowV2Container';
import { StepEscalateDescription } from './StepEscalateDescription';
import { StepEscalationActions } from './StepEscalationActions';
import { StepLiveTalkdown } from './StepLiveTalkdown';
import { TalkdownSteps } from './TalkdownSteps';
import { SOPData } from './useSOPV2Data';
import { SOPV2Action, SOPV2State, SOPV2Step } from './useSOPV2Machine';

const NO_SOP_MESSAGE =
  'No SOP is configured for this site. Please ask your admin to add one.';

interface Props extends SOPWorkflowV2ContainerProps {
  state: SOPV2State;
  dispatch: Dispatch<SOPV2Action>;
  sop: SOPData;
  location?: Location;
  isScanContext: boolean;
  resolvedProduct: UnifiedMonitoringProduct;
  hideAIDetails: boolean;
  talkdownCameras: LocationAlarmCamera[];
  talkdownSteps: TalkdownStep[];
  onQuickResolve: (label: string, action?: QuickResolveActionItem) => void;
  onEscalated: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  onAddEvent: (action: TopSituation) => void;
}

export function SOPV2Panels(props: Props) {
  const {
    state,
    dispatch,
    sop,
    location,
    isScanContext,
    resolvedProduct,
    hideAIDetails,
    talkdownCameras,
    talkdownSteps,
    onQuickResolve,
    onEscalated,
    onAddEvent,
    locationId,
    locationAlarmId,
    locationTenantId,
    locationTimezone,
    product,
    escalationId,
    escalation,
    cameraNames,
    aiOperatorRep,
    unauthorizedFaces,
    onResolveEscalation,
    alarmGroupStatus,
  } = props;

  const {
    sopWorkflow,
    sopData,
    isLoading,
    isError,
    cards,
    freshness,
    hasNoSOP,
  } = sop;
  const isOverview = state.step === SOPV2Step.OVERVIEW;
  const readOnly = isAlarmGroupResolved(alarmGroupStatus);

  const activeTalkdownIndex = state.talkdownStepsDone + 1;
  const isTalkdownStep = state.step === SOPV2Step.LIVE_TALKDOWN;
  const visibleTalkdownSteps = (isTalkdownStep && talkdownSteps) || [];
  const showFaces = !!unauthorizedFaces && unauthorizedFaces.length > 0;
  const nestedClass = aiOperatorRep?.recommendations
    ? undefined
    : clsx(
        CARD_ROOT_CLASS,
        CARD_VARIANTS.surface.root,
        CARD_VARIANTS.surface.padding
      );

  const showSOPError = isError && !sopWorkflow;
  const emptyMessage = hasNoSOP && NO_SOP_MESSAGE;
  const scanAlarmGroupId = isScanContext && locationAlarmId;

  return (
    <>
      {readOnly && (
        <div className="pt-2">
          <Alert type="success" size="small">
            This alarm group is already resolved
          </Alert>
        </div>
      )}

      {!readOnly && isOverview && !isLoading && (
        <ActionsRow
          quickResolveActions={sopWorkflow?.quickResolveActions}
          onQuickResolve={onQuickResolve}
          onEscalate={() => dispatch({ type: 'ESCALATE_CLICKED' })}
          isZeroToleranceSite={sopWorkflow?.isZeroTolerance}
        />
      )}

      <div className={clsx('space-y-3', state.escalationCommitted && 'mt-3')}>
        <AIRecommendationCard
          alarmId={locationAlarmId}
          product={resolvedProduct}
          timeZone={locationTimezone}
          hideDetails={hideAIDetails}
          collapsible={state.step === SOPV2Step.ESCALATION_ACTIONS}
        >
          {showFaces && (
            <div className={nestedClass}>
              <UnauthorizedFaceDetection
                faces={unauthorizedFaces}
                timeZone={locationTimezone}
                headingClass="text-alert-500"
              />
            </div>
          )}
          {visibleTalkdownSteps.length > 0 && (
            <div className={nestedClass}>
              <TalkdownSteps
                steps={visibleTalkdownSteps}
                activeIndex={activeTalkdownIndex}
              />
            </div>
          )}
        </AIRecommendationCard>
      </div>

      {isLoading && (
        <div className="flex justify-center p-4">
          <LoadingIndicator />
        </div>
      )}

      {showSOPError && (
        <Alert type="error" size="small">
          Could not load the SOP for this site.
        </Alert>
      )}

      {!isLoading && !showSOPError && (
        <SOPCard
          cards={cards}
          freshness={freshness}
          emptyMessage={emptyMessage || undefined}
        />
      )}

      {!readOnly && (
        <div className={clsx(isOverview && 'pb-14')}>
          <LastEscalatedCard locationId={locationId} />
        </div>
      )}

      {state.step === SOPV2Step.ESCALATE_DESCRIPTION && (
        <StepEscalateDescription
          locationId={locationId}
          locationAlarmId={locationAlarmId}
          selectedSituation={state.selectedSituation}
          product={product}
          isAwaitingEscalation={!!state.pendingEscalation}
          onEscalated={onEscalated}
          onBack={() => dispatch({ type: 'BACK' })}
        />
      )}

      {state.step === SOPV2Step.LIVE_TALKDOWN && (
        <StepLiveTalkdown
          cameras={talkdownCameras}
          locationAlarmId={locationAlarmId}
          escalationId={escalationId}
          product={product}
          steps={talkdownSteps}
          stepsDone={state.talkdownStepsDone}
          onSent={() =>
            dispatch({
              type: 'TALKDOWN_SENT',
              totalSteps: talkdownSteps.length,
            })
          }
          onSkip={() => dispatch({ type: 'TALKDOWN_FINISHED' })}
          onProceed={() => dispatch({ type: 'TALKDOWN_FINISHED' })}
        />
      )}

      {state.step === SOPV2Step.ESCALATION_ACTIONS && (
        <StepEscalationActions
          locationId={locationId}
          locationAlarmId={locationAlarmId}
          alarmGroupId={scanAlarmGroupId || undefined}
          locationTenantId={locationTenantId}
          escalationId={escalationId}
          siteAddress={sopWorkflow?.siteAddress ?? ''}
          mapEmbedUrl={sopData?.map_embed_url}
          product={product}
          timezone={locationTimezone}
          escalation={escalation}
          location={location}
          cameraNames={cameraNames}
          aiOperatorRep={aiOperatorRep}
          onAddEvent={onAddEvent}
          onResolveEscalation={onResolveEscalation}
        />
      )}
    </>
  );
}

export default SOPV2Panels;
